/**
 * Mood State Definitions for Avatar - Research-Based Psychology
 * Maps sentiment/emotion to facial expressions and body language
 * Uses ARKit blendshapes for facial control
 *
 * Based on Facial Action Coding System (FACS) and emotional psychology:
 * - Genuine emotions have specific muscle patterns
 * - Asymmetry signals confusion/skepticism
 * - Inner brow raises = sadness/empathy
 * - Cheek raises = genuine smile (Duchenne)
 */

import type { Sentiment } from '../utils/sentimentAnalyzer';

export interface MoodState {
  name: Sentiment;
  description: string;
  blendshapes: {
    [key: string]: number;
  };
  bodyLanguage?: {
    headTilt?: number;    // X-axis rotation
    headTurn?: number;    // Y-axis rotation
    eyeGaze?: {
      x: number;
      y: number;
    };
  };
}

/**
 * Mood state definitions with blendshape values
 * Values are 0-1 range for morph target influences
 */
export const MOOD_STATES: Record<Sentiment, MoodState> = {

  neutral: {
    name: 'neutral',
    description: 'Calm, attentive, professional demeanor',
    blendshapes: {
      // Minimal expression - attentive but neutral
      browInnerUp: 0.05,
      eyeWide_L: 0.03,
      eyeWide_R: 0.03,
      mouthSmile_L: 0.02,
      mouthSmile_R: 0.02
    },
    bodyLanguage: {
      headTilt: 0,
      headTurn: 0,
      eyeGaze: { x: 0, y: 0 }
    }
  },

  positive: {
    name: 'positive',
    description: 'Warm, friendly, encouraging expression',
    blendshapes: {
      // Genuine smile with raised cheeks - warm without overacting
      mouthSmile_L: 0.60,
      mouthSmile_R: 0.60,
      cheekSquint_L: 0.34,
      cheekSquint_R: 0.34,
      mouthDimple_L: 0.28,
      mouthDimple_R: 0.28,
      // Raised, relaxed eyebrows
      browInnerUp: 0.35,
      browOuterUp_L: 0.32,
      browOuterUp_R: 0.32,
      // Wider eyes (interest and warmth)
      eyeWide_L: 0.28,
      eyeWide_R: 0.28
    },
    bodyLanguage: {
      headTilt: 0.02,  // Slight head tilt (warmth)
      headTurn: 0,
      eyeGaze: { x: 0, y: 0.02 } // Direct, friendly gaze
    }
  },

  concerned: {
    name: 'concerned',
    description: 'Serious, attentive, problem-solving focus',
    blendshapes: {
      // Furrowed brow (concentration/concern) - VERY STRONG
      browDown_L: 0.65,
      browDown_R: 0.65,
      browInnerUp: 0.45,  // Inner brow up (concern)
      // Pressed lips
      mouthPress_L: 0.35,
      mouthPress_R: 0.35,
      mouthClose: 0.40,
      // Focused, intense eyes
      eyeSquint_L: 0.35,
      eyeSquint_R: 0.35,
      // Mouth downturn - VISIBLE
      mouthFrown_L: 0.35,
      mouthFrown_R: 0.35
    },
    bodyLanguage: {
      headTilt: 0,
      headTurn: 0,
      eyeGaze: { x: 0, y: 0.03 } // Focused, direct gaze
    }
  },

  confused: {
    name: 'confused',
    description: 'Puzzled, seeking clarification - EXTREME asymmetric expression',
    blendshapes: {
      // EXTREME asymmetric brow (classic confusion - one way up, one way down)
      browInnerUp: 0.80,         // VERY HIGH (questioning)
      browOuterUp_L: 0.65,       // LEFT WAY UP (questioning)
      browOuterUp_R: 0.15,       // RIGHT DOWN (skeptical)
      browDown_L: 0.10,          // Left relaxed
      browDown_R: 0.60,          // RIGHT WAY DOWN - INCREASED asymmetry
      // MORE pursed lips (puzzled "huh?")
      mouthPucker: 0.50,         // INCREASED - more pronounced
      mouthFunnel: 0.30,         // INCREASED
      // Squinted eyes (concentrating, trying to understand)
      eyeSquint_L: 0.50,         // INCREASED
      eyeSquint_R: 0.45,         // INCREASED
      // Slight asymmetry in mouth too
      mouthLeft: 0.10            // ADDED - pulls mouth to side (puzzled)
    },
    bodyLanguage: {
      headTilt: 0.10,  // INCREASED - more pronounced "huh?" gesture
      headTurn: -0.03,
      eyeGaze: { x: -0.05, y: 0.05 } // Looking up-left (thinking/searching)
    }
  },

  empathetic: {
    name: 'empathetic',
    description: 'Understanding, compassionate, supportive - "I feel for you"',
    blendshapes: {
      // STRONG "sad eyebrows" (empathy = sadness for others + warmth)
      browInnerUp: 0.40,         // DOUBLED - creates caring/sad angle
      browOuterUp_L: 0.12,       // INCREASED
      browOuterUp_R: 0.12,       // INCREASED
      // Bittersweet expression (smile + slight sadness)
      mouthSmile_L: 0.12,        // Slight warm smile
      mouthSmile_R: 0.12,
      mouthFrown_L: 0.15,        // ADDED - creates bittersweet look
      mouthFrown_R: 0.15,
      // Soft, concerned eyes
      eyeSquint_L: 0.12,         // DOUBLED - shows concern
      eyeSquint_R: 0.12,
      eyeWide_L: 0.08,           // Slightly open (engaged)
      eyeWide_R: 0.08,
      // Gentle smile muscles
      mouthDimple_L: 0.10,
      mouthDimple_R: 0.10,
      cheekSquint_L: 0.10,
      cheekSquint_R: 0.10
    },
    bodyLanguage: {
      headTilt: 0.06,  // INCREASED - more pronounced sympathetic tilt
      headTurn: 0,
      eyeGaze: { x: 0, y: 0 } // Direct, caring gaze
    }
  },

  negative: {
    name: 'negative',
    description: 'Apologetic, regretful, acknowledging bad news - STRONG sad expression',
    blendshapes: {
      // VERY STRONG "sad face" (regret/apology)
      browInnerUp: 0.50,         // MUCH HIGHER - classic sad eyebrows
      browOuterUp_L: 0.15,       // ADDED - creates sad angle
      browOuterUp_R: 0.15,       // ADDED
      browDown_L: 0.25,          // INCREASED - sad angle
      browDown_R: 0.25,          // INCREASED
      // STRONG frown
      mouthFrown_L: 0.40,        // MUCH STRONGER - visible sadness
      mouthFrown_R: 0.40,
      mouthPress_L: 0.25,        // INCREASED - tension
      mouthPress_R: 0.25,
      // Concerned, apologetic eyes
      eyeSquint_L: 0.20,         // INCREASED - shows concern
      eyeSquint_R: 0.20,
      // Tense mouth
      mouthPucker: 0.15,         // INCREASED
      mouthClose: 0.20,          // INCREASED
      mouthLowerDown_L: 0.15,    // ADDED - pulls mouth down
      mouthLowerDown_R: 0.15
    },
    bodyLanguage: {
      headTilt: -0.04,  // DOUBLED - more submission/acknowledgment
      headTurn: 0,
      eyeGaze: { x: 0, y: -0.04 } // DOUBLED - more downward (apologetic)
    }
  },

  // ============================================================
  // NEW MOOD STATES - Sprint 20.5 Enhanced Emotion Support
  // ============================================================

  celebration: {
    name: 'celebration',
    description: 'Joy, excitement, celebrating success - BIG smile with raised eyebrows',
    blendshapes: {
      // Clear genuine smile - celebratory without pushing the rig to extremes
      mouthSmile_L: 0.68,
      mouthSmile_R: 0.68,
      cheekSquint_L: 0.48,       // Eyes crinkle with genuine joy
      cheekSquint_R: 0.48,
      mouthDimple_L: 0.35,
      mouthDimple_R: 0.35,
      // Raised eyebrows (surprise + joy)
      browInnerUp: 0.50,
      browOuterUp_L: 0.46,
      browOuterUp_R: 0.46,
      // Wide eyes (excitement)
      eyeWide_L: 0.40,
      eyeWide_R: 0.40,
      // Slightly open mouth (joyful exclamation)
      jawOpen: 0.12,
      mouthUpperUp_L: 0.15,
      mouthUpperUp_R: 0.15
    },
    bodyLanguage: {
      headTilt: 0.05,  // Slight excited tilt
      headTurn: 0,
      eyeGaze: { x: 0, y: 0.05 } // Looking up slightly (optimistic)
    }
  },

  concentration: {
    name: 'concentration',
    description: 'Deep focus, processing complex information - thinking expression',
    blendshapes: {
      // Slight brow furrow (focus, not anger)
      browDown_L: 0.35,
      browDown_R: 0.35,
      browInnerUp: 0.20,         // Creates "thinking" angle
      // Narrowed eyes (focus)
      eyeSquint_L: 0.40,
      eyeSquint_R: 0.40,
      // Slight tension in lips (concentration)
      mouthPress_L: 0.20,
      mouthPress_R: 0.20,
      mouthPucker: 0.10,
      // Looking slightly off (accessing memory/thinking)
      mouthClose: 0.25
    },
    bodyLanguage: {
      headTilt: 0.02,
      headTurn: -0.02,           // Slight turn (thoughtful)
      eyeGaze: { x: -0.05, y: 0.08 } // Looking up-left (recalling/thinking)
    }
  },

  surprised: {
    name: 'surprised',
    description: 'Unexpected information - raised eyebrows, wide eyes, open mouth',
    blendshapes: {
      // VERY raised eyebrows (classic surprise)
      browInnerUp: 0.80,
      browOuterUp_L: 0.75,
      browOuterUp_R: 0.75,
      // Wide open eyes
      eyeWide_L: 0.70,
      eyeWide_R: 0.70,
      // Open mouth (gasp)
      jawOpen: 0.35,
      mouthFunnel: 0.25,
      // Slight "O" mouth shape
      mouthPucker: 0.15
    },
    bodyLanguage: {
      headTilt: -0.03,           // Slight backward lean
      headTurn: 0,
      eyeGaze: { x: 0, y: 0.02 } // Direct, engaged gaze
    }
  },

  frustrated: {
    name: 'frustrated',
    description: 'Visible frustration - escalation trigger state for empathetic response',
    blendshapes: {
      // Strong furrowed brow (anger/frustration)
      browDown_L: 0.75,
      browDown_R: 0.75,
      browInnerUp: 0.30,         // Tension
      // Tight, tense lips
      mouthPress_L: 0.50,
      mouthPress_R: 0.50,
      mouthClose: 0.40,
      // Slight sneer (disgust/frustration)
      noseSneer_L: 0.25,
      noseSneer_R: 0.25,
      // Narrowed eyes (intensity)
      eyeSquint_L: 0.45,
      eyeSquint_R: 0.45,
      // Tension in jaw
      mouthFrown_L: 0.35,
      mouthFrown_R: 0.35,
      mouthStretch_L: 0.20,
      mouthStretch_R: 0.20
    },
    bodyLanguage: {
      headTilt: 0,               // Direct, no tilt
      headTurn: 0,
      eyeGaze: { x: 0, y: 0 }    // Intense direct gaze
    }
  }
};

/**
 * Get blendshape values for a specific mood
 * @param mood - The mood state
 * @param intensity - Intensity multiplier (0-1)
 * @returns Blendshape values ready to apply
 */
export function getMoodBlendshapes(
  mood: Sentiment,
  intensity: number = 1.0
): { [key: string]: number } {

  const moodState = MOOD_STATES[mood];
  if (!moodState) return {};

  // Apply intensity multiplier
  const blendshapes: { [key: string]: number } = {};
  Object.entries(moodState.blendshapes).forEach(([key, value]) => {
    blendshapes[key] = value * intensity;
  });

  return blendshapes;
}

/**
 * Blend between two mood states
 * Used for smooth transitions
 * @param fromMood - Starting mood
 * @param toMood - Target mood
 * @param progress - Blend progress (0-1)
 * @returns Blended blendshape values
 */
export function blendMoodStates(
  fromMood: Sentiment,
  toMood: Sentiment,
  progress: number
): { [key: string]: number } {

  const fromBlendshapes = MOOD_STATES[fromMood]?.blendshapes || {};
  const toBlendshapes = MOOD_STATES[toMood]?.blendshapes || {};

  // Get all unique blendshape keys
  const allKeys = new Set([
    ...Object.keys(fromBlendshapes),
    ...Object.keys(toBlendshapes)
  ]);

  const blended: { [key: string]: number } = {};

  allKeys.forEach(key => {
    const fromValue = fromBlendshapes[key] || 0;
    const toValue = toBlendshapes[key] || 0;
    blended[key] = fromValue + (toValue - fromValue) * progress;
  });

  return blended;
}

